import React, { useEffect, useState } from "react";
import { useI18n } from "../../i18n/I18nContext";
import { ResourceManager } from "../../components/ResourceManager";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "branch_manager", label_ar: "مدير فرع", label_en: "Branch Manager" },
  { value: "cashier", label_ar: "كاشير", label_en: "Cashier" },
  { value: "photographer", label_ar: "مصور", label_en: "Photographer" },
  { value: "customer_service", label_ar: "خدمة عملاء", label_en: "Customer Service" },
  { value: "accountant", label_ar: "محاسب", label_en: "Accountant" },
];

export function ServicesAdmin() {
  const { t, isAr, localize, fmtSAR } = useI18n();
  return (
    <div className="space-y-10">
      <ResourceManager
        title={isAr ? "الخدمات" : "Services"}
        subtitle={isAr ? "أضف وعدّل الخدمات ومدتها وسعرها وفروعها" : "Add and edit services, duration, price and branches"}
        endpoint="services"
        relations={{ category_id: { endpoint: "categories", labelField: "name" }, branch_ids: { endpoint: "branches", labelField: "name" } }}
        columns={[
          { key: "name", label: isAr ? "الاسم" : "Name", bilingual: true },
          { key: "price", label: isAr ? "السعر" : "Price", render: (r) => fmtSAR(r.price) },
          { key: "duration_minutes", label: isAr ? "المدة (د)" : "Duration" },
          { key: "full_studio", label: isAr ? "استوديو كامل" : "Full studio" },
          { key: "active", label: isAr ? "مفعّل" : "Active" },
        ]}
        fields={[
          { key: "category_id", type: "select", relation: true, label_ar: "الفئة", label_en: "Category" },
          { key: "name", type: "bilingual", label_ar: "الاسم", label_en: "Name" },
          { key: "desc", type: "bilingual-textarea", label_ar: "الوصف", label_en: "Description" },
          { key: "price", type: "number", label_ar: "السعر الأساسي (ر.س)", label_en: "Base price (SAR)" },
          { key: "duration_minutes", type: "number", label_ar: "مدة الجلسة (دقيقة)", label_en: "Duration (min)" },
          { key: "prep_minutes", type: "number", label_ar: "وقت التجهيز (دقيقة)", label_en: "Prep (min)" },
          { key: "cleanup_minutes", type: "number", label_ar: "وقت التنظيف (دقيقة)", label_en: "Cleanup (min)" },
          { key: "image", type: "image", label_ar: "صورة الخدمة", label_en: "Image" },
          { key: "branch_ids", type: "multiselect", label_ar: "الفروع المتاحة (فارغ = الكل)", label_en: "Branches (empty = all)" },
          { key: "full_studio", type: "checkbox", label_ar: "يحجز الاستوديو كاملاً", label_en: "Books full studio" },
          { key: "active", type: "checkbox", label_ar: "مفعّل", label_en: "Active" },
        ]}
      />
      <ResourceManager
        title={isAr ? "الباقات" : "Packages"}
        endpoint="packages"
        relations={{ service_id: { endpoint: "services", labelField: "name" } }}
        columns={[{ key: "name", label: isAr ? "الاسم" : "Name", bilingual: true }, { key: "price", label: isAr ? "السعر" : "Price", render: (r) => fmtSAR(r.price) }]}
        fields={[
          { key: "service_id", type: "select", relation: true, label_ar: "الخدمة", label_en: "Service" },
          { key: "name", type: "bilingual", label_ar: "الاسم", label_en: "Name" },
          { key: "desc", type: "bilingual", label_ar: "الوصف", label_en: "Description" },
          { key: "price", type: "number", label_ar: "السعر (ر.س)", label_en: "Price (SAR)" },
        ]}
      />
      <ResourceManager
        title={isAr ? "الإضافات" : "Add-ons"}
        endpoint="addons"
        relations={{ service_id: { endpoint: "services", labelField: "name" } }}
        columns={[{ key: "name", label: isAr ? "الاسم" : "Name", bilingual: true }, { key: "price", label: isAr ? "السعر" : "Price", render: (r) => fmtSAR(r.price) }]}
        fields={[
          { key: "service_id", type: "select", relation: true, label_ar: "الخدمة (فارغ = عام)", label_en: "Service (empty = global)" },
          { key: "name", type: "bilingual", label_ar: "الاسم", label_en: "Name" },
          { key: "price", type: "number", label_ar: "السعر (ر.س)", label_en: "Price (SAR)" },
        ]}
      />
    </div>
  );
}

export function BranchesAdmin() {
  const { isAr, fmtSAR } = useI18n();
  return (
    <div className="space-y-10">
      <ResourceManager
        title={isAr ? "الفروع" : "Branches"}
        subtitle={isAr ? "أضف الفروع وبياناتها" : "Manage branches"}
        endpoint="branches"
        columns={[{ key: "name", label: isAr ? "الاسم" : "Name", bilingual: true }, { key: "city", label: isAr ? "المدينة" : "City", bilingual: true }, { key: "phone", label: isAr ? "الهاتف" : "Phone" }, { key: "active", label: isAr ? "مفعّل" : "Active" }]}
        fields={[
          { key: "name", type: "bilingual", label_ar: "اسم الفرع", label_en: "Branch name" },
          { key: "city", type: "bilingual", label_ar: "المدينة", label_en: "City" },
          { key: "address", type: "bilingual", label_ar: "العنوان", label_en: "Address" },
          { key: "phone", type: "text", label_ar: "الهاتف", label_en: "Phone" },
          { key: "location", type: "map", label_ar: "الموقع على الخريطة", label_en: "Map location" },
          { key: "active", type: "checkbox", label_ar: "مفعّل", label_en: "Active" },
        ]}
      />
      <ResourceManager
        title={isAr ? "الموارد (استوديوهات ومصورون)" : "Resources (rooms & photographers)"}
        subtitle={isAr ? "الموارد المستخدمة لمنع تعارض الحجوزات" : "Resources used for booking conflict prevention"}
        endpoint="resources"
        relations={{ branch_id: { endpoint: "branches", labelField: "name" } }}
        columns={[{ key: "name", label: isAr ? "الاسم" : "Name", bilingual: true }, { key: "type", label: isAr ? "النوع" : "Type" }, { key: "active", label: isAr ? "مفعّل" : "Active" }]}
        fields={[
          { key: "branch_id", type: "select", relation: true, label_ar: "الفرع", label_en: "Branch" },
          { key: "type", type: "select", options: [{ value: "room", label_ar: "استوديو", label_en: "Room" }, { value: "photographer", label_ar: "مصور", label_en: "Photographer" }, { value: "equipment", label_ar: "معدات", label_en: "Equipment" }], label_ar: "النوع", label_en: "Type" },
          { key: "name", type: "bilingual", label_ar: "الاسم", label_en: "Name" },
          { key: "active", type: "checkbox", label_ar: "مفعّل", label_en: "Active" },
        ]}
      />
    </div>
  );
}

export function ProductsAdmin() {
  const { isAr, fmtSAR } = useI18n();
  return (
    <ResourceManager
      title={isAr ? "منتجات المتجر" : "Store Products"}
      subtitle={isAr ? "الطبعات والألبومات والإطارات" : "Prints, albums, frames"}
      endpoint="products"
      columns={[{ key: "name", label: isAr ? "الاسم" : "Name", bilingual: true }, { key: "price", label: isAr ? "السعر" : "Price", render: (r) => fmtSAR(r.price) }, { key: "sku", label: "SKU" }, { key: "active", label: isAr ? "مفعّل" : "Active" }]}
      fields={[
        { key: "name", type: "bilingual", label_ar: "الاسم", label_en: "Name" },
        { key: "price", type: "number", label_ar: "السعر (ر.س)", label_en: "Price (SAR)" },
        { key: "sku", type: "text", label_ar: "رمز SKU", label_en: "SKU" },
        { key: "image", type: "image", label_ar: "الصورة", label_en: "Image" },
        { key: "active", type: "checkbox", label_ar: "مفعّل", label_en: "Active" },
      ]}
    />
  );
}

export function CouponsAdmin() {
  const { isAr } = useI18n();
  return (
    <ResourceManager
      title={isAr ? "الكوبونات" : "Coupons"}
      endpoint="coupons"
      columns={[{ key: "code", label: "Code" }, { key: "type", label: isAr ? "النوع" : "Type" }, { key: "value", label: isAr ? "القيمة" : "Value" }, { key: "uses_left", label: isAr ? "المتبقي" : "Uses" }, { key: "active", label: isAr ? "مفعّل" : "Active" }]}
      fields={[
        { key: "code", type: "text", label_ar: "الكود", label_en: "Code", help: isAr ? "بأحرف كبيرة" : "Uppercase" },
        { key: "type", type: "select", options: [{ value: "percent", label_ar: "نسبة %", label_en: "Percent %" }, { value: "amount", label_ar: "مبلغ ثابت", label_en: "Fixed amount" }], label_ar: "النوع", label_en: "Type" },
        { key: "value", type: "number", label_ar: "القيمة", label_en: "Value" },
        { key: "min_amount", type: "number", label_ar: "أدنى مبلغ للطلب", label_en: "Min order amount" },
        { key: "uses_left", type: "number", label_ar: "عدد الاستخدامات", label_en: "Uses left" },
        { key: "active", type: "checkbox", label_ar: "مفعّل", label_en: "Active" },
      ]}
    />
  );
}

export function GalleryAdmin() {
  const { isAr } = useI18n();
  return (
    <ResourceManager
      title={isAr ? "معرض الأعمال" : "Gallery"}
      endpoint="gallery"
      columns={[{ key: "image", label: isAr ? "الصورة" : "Image", render: (r) => <img src={r.image} alt="" className="h-10 w-16 rounded object-cover" /> }, { key: "category", label: isAr ? "التصنيف" : "Category" }, { key: "order", label: isAr ? "الترتيب" : "Order" }]}
      fields={[
        { key: "image", type: "image", label_ar: "الصورة", label_en: "Image" },
        { key: "category", type: "text", label_ar: "التصنيف", label_en: "Category" },
        { key: "title", type: "bilingual", label_ar: "العنوان", label_en: "Title" },
        { key: "order", type: "number", label_ar: "الترتيب", label_en: "Order" },
      ]}
    />
  );
}

export function FaqAdmin() {
  const { isAr } = useI18n();
  return (
    <ResourceManager
      title={isAr ? "الأسئلة الشائعة" : "FAQ"}
      endpoint="faq"
      columns={[{ key: "q_ar", label: isAr ? "السؤال" : "Question", render: (r) => isAr ? r.q_ar : r.q_en }, { key: "order", label: isAr ? "الترتيب" : "Order" }]}
      fields={[
        { key: "q", type: "bilingual", label_ar: "السؤال", label_en: "Question" },
        { key: "a", type: "bilingual-textarea", label_ar: "الإجابة", label_en: "Answer" },
        { key: "order", type: "number", label_ar: "الترتيب", label_en: "Order" },
      ]}
    />
  );
}

export function MembershipsAdmin() {
  const { isAr, fmtSAR } = useI18n();
  return (
    <ResourceManager
      title={isAr ? "باقات العضوية" : "Membership Plans"}
      endpoint="membership_plans"
      columns={[{ key: "name", label: isAr ? "الاسم" : "Name", bilingual: true }, { key: "price", label: isAr ? "السعر" : "Price", render: (r) => fmtSAR(r.price) }, { key: "sessions", label: isAr ? "الجلسات" : "Sessions" }]}
      fields={[
        { key: "name", type: "bilingual", label_ar: "الاسم", label_en: "Name" },
        { key: "type", type: "select", options: [{ value: "monthly", label_ar: "شهري", label_en: "Monthly" }, { value: "quarterly", label_ar: "ربع سنوي", label_en: "Quarterly" }, { value: "yearly", label_ar: "سنوي", label_en: "Yearly" }, { value: "sessions", label_ar: "باقة جلسات", label_en: "Session pack" }], label_ar: "النوع", label_en: "Type" },
        { key: "price", type: "number", label_ar: "السعر (ر.س)", label_en: "Price (SAR)" },
        { key: "sessions", type: "number", label_ar: "عدد الجلسات", label_en: "Sessions" },
        { key: "validity_days", type: "number", label_ar: "الصلاحية (يوم)", label_en: "Validity (days)" },
      ]}
    />
  );
}

// Staff needs dedicated add form (password + role), delete via new endpoint
export function StaffAdmin() {
  const { t, isAr } = useI18n();
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const load = () => api.get("/admin/staff").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)); }, []);
  const openNew = () => { setEditing(null); setForm({ role: "photographer", branch_ids: [], active: true }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r, password: "" }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/admin/staff/${editing.id}`, form);
      else await api.post("/admin/staff", form);
      toast.success(t("common.save")); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (r) => { if (!window.confirm(isAr ? "حذف؟" : "Delete?")) return; try { await api.delete(`/admin/staff/${r.id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const base = "mt-1 w-full rounded-xl border border-border px-3 py-2.5 text-sm";
  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">{t("admin.staff")}</h1>
        <button onClick={openNew} data-testid="add-staff-btn" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> {t("common.add")}</button>
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-white">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="border-b border-border bg-slate-50 text-xs text-slate-500"><tr>{[t("common.name"), t("common.email"), t("admin.roles"), ""].map((h, i) => <th key={i} className="px-4 py-3 text-start font-medium">{h}</th>)}</tr></thead>
          <tbody>{rows.map((s) => (
            <tr key={s.id} data-testid={`staff-row-${s.id}`} className="border-b border-border last:border-0">
              <td className="px-4 py-3 font-medium">{s.name}</td><td className="px-4 py-3 text-slate-600" dir="ltr">{s.email}</td>
              <td className="px-4 py-3"><span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">{s.role}</span></td>
              <td className="px-4 py-3"><div className="flex justify-end gap-1"><button onClick={() => openEdit(s)} className="rounded-md border border-border p-1.5 hover:border-primary"><Pencil className="h-3.5 w-3.5" /></button>{s.role !== "super_admin" && <button onClick={() => del(s)} className="rounded-md border border-border p-1.5 text-[hsl(var(--brand-red))]"><Trash2 className="h-3.5 w-3.5" /></button>}</div></td>
            </tr>))}</tbody>
        </table>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex justify-between"><h3 className="text-lg font-bold">{editing ? t("common.edit") : t("common.add")}</h3><button onClick={() => setOpen(false)}><X className="h-5 w-5" /></button></div>
            <div className="space-y-3">
              <div><label className="text-sm font-medium">{t("common.name")}</label><input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-field-name" className={base} /></div>
              <div><label className="text-sm font-medium">{t("common.email")}</label><input dir="ltr" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-field-email" className={base} disabled={!!editing} /></div>
              <div><label className="text-sm font-medium">{t("auth.password")} {editing && <span className="text-xs text-slate-400">{isAr ? "(اتركه فارغاً للإبقاء)" : "(blank to keep)"}</span>}</label><input dir="ltr" type="text" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-field-password" className={base} /></div>
              <div><label className="text-sm font-medium">{t("admin.roles")}</label>
                <select value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-field-role" className={base}>
                  {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{isAr ? o.label_ar : o.label_en}</option>)}
                </select></div>
              <div><label className="text-sm font-medium">{t("admin.branches")}</label>
                <div className="mt-1 flex flex-wrap gap-2">{branches.map((b) => { const sel = (form.branch_ids || []).includes(b.id); return <button type="button" key={b.id} onClick={() => setForm({ ...form, branch_ids: sel ? form.branch_ids.filter((x) => x !== b.id) : [...(form.branch_ids || []), b.id] })} className={`rounded-full border px-3 py-1.5 text-xs ${sel ? "border-primary bg-primary/10 text-primary" : "border-border text-slate-600"}`}>{isAr ? b.name_ar : b.name_en}</button>; })}</div></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium">{isAr ? "رقم الإقامة" : "Iqama No."}</label><input dir="ltr" value={form.iqama || ""} onChange={(e) => setForm({ ...form, iqama: e.target.value })} data-testid="staff-field-iqama" className={base} /></div>
                <div><label className="text-sm font-medium">{isAr ? "انتهاء الإقامة" : "Iqama expiry"}</label><input type="date" value={form.iqama_expiry || ""} onChange={(e) => setForm({ ...form, iqama_expiry: e.target.value })} className={base} /></div>
                <div><label className="text-sm font-medium">{isAr ? "رقم الرخصة" : "License No."}</label><input dir="ltr" value={form.license || ""} onChange={(e) => setForm({ ...form, license: e.target.value })} className={base} /></div>
                <div><label className="text-sm font-medium">{isAr ? "انتهاء الرخصة" : "License expiry"}</label><input type="date" value={form.license_expiry || ""} onChange={(e) => setForm({ ...form, license_expiry: e.target.value })} className={base} /></div>
                <div><label className="text-sm font-medium">{isAr ? "بداية العقد" : "Contract start"}</label><input type="date" value={form.contract_start || ""} onChange={(e) => setForm({ ...form, contract_start: e.target.value })} data-testid="staff-field-contract" className={base} /></div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2"><button onClick={() => setOpen(false)} className="rounded-full px-5 py-2 text-sm text-slate-600">{t("common.cancel")}</button><button onClick={save} data-testid="staff-save-btn" className="rounded-full bg-primary px-6 py-2 text-sm font-semibold text-white">{t("common.save")}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
